import { useEffect, useRef } from 'react';

/**
 * Плашка «Отменить» — шесть секунд.
 *
 * Обратимые действия выполняются сразу и показывают отмену; диалог с подтверждением
 * остаётся только там, где отмена невозможна ([D-12](docs/DECISIONS.md)).
 *
 * Почему именно шесть. На Android плашка с кнопкой висела **бесконечно** и закрывала
 * список, пока её не смахнёшь; четыре секунды — не успеть прочитать и попасть пальцем
 * на ходу, десять — мешает работать. Число одно на все места с отменой.
 */
const UNDO_MS = 6000;

export function AppUndoSnackbar({
  text,
  onUndo,
  onExpire,
  actionLabel = 'Отменить',
}: {
  text: string;
  /** Нажали «Отменить»: действие откатывается, экран остаётся на месте. */
  onUndo: () => void;
  /**
   * Время вышло, и отменять уже нечего.
   *
   * Разделено с [onUndo] намеренно: у карточки удалённого дела это разные исходы —
   * отменил и остался, либо не отменил и ушёл. Пока это был один вызов, «Отменить»
   * восстанавливало дело **и тут же уводило** из него.
   */
  onExpire: () => void;
  /**
   * Название действия. По умолчанию «Отменить»; сообщение об ошибке отменять нечего,
   * и там стоит «Понятно» — плашка одна на оба случая, чтобы не заводить вторую.
   */
  actionLabel?: string;
}) {
  const timer = useRef<number | null>(null);

  useEffect(() => {
    timer.current = window.setTimeout(onExpire, UNDO_MS);

    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [text, onExpire]);

  /**
   * Отмена гасит таймер **сразу**, не дожидаясь, пока отработает сам откат.
   *
   * Иначе нажатие на последней секунде даёт оба исхода разом: дело возвращается
   * и экран всё равно закрывается. На медленном устройстве, где откат занимает время,
   * это не редкость, а норма.
   */
  const undo = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    onUndo();
  };

  return (
    <div className="snackbar" role="status">
      <span className="snackbar__text">{text}</span>
      <button type="button" className="snackbar__action" onClick={undo}>
        {actionLabel}
      </button>
    </div>
  );
}
